// components
import { GridCompact6 } from 'components/shared/Grid/Grid.components';
import SectionHeader from '../SectionHeader/SectionHeader';
import EntitySearch, {
  EmptyQuerySearchOptions
} from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';
import { EntitySearchInput } from 'components/mrp/components/EntitySearch/components/EntitySearchInput';
import ClickHandler from 'components/mrp/components/Popover/components/ClickHandler/ClickHandler';
import SelectedEntity from './components/SelectedEntity/SelectedEntity';

// hooks
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

// store/context
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';
import { ShortcutContext } from 'components/shared/ShortcutProvider/ShortcutContext';

// types
import { MrpEntityKey, EntitySectionProps } from './EntitySection.types';
import { AppointmentFormFieldData } from '../../AppointmentForm.types';
import { PlanningEntityIdentifier } from 'generated/index';
import { getEntityIcon } from 'components/mrp/mrp.utils';

const EntitySection = ({ planningEntityType }: EntitySectionProps) => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { calendarsObj, openCalendar } = useCalendarStore(
    useShallow((state) => ({
      calendarsObj: state.calendars,
      openCalendar: state.control.openCalendar
    }))
  );

  const [isEntityPopoverOpen, setIsEntityPopoverOpen] = useState(false);
  const dummyInputRef = useRef<HTMLInputElement | null>(null);
  const [initialQuery, setInitialQuery] = useState<string>('');

  const { setActiveShortcutContexts, shortcuts } = useContext(ShortcutContext);

  const calendars = Object.values(calendarsObj);

  useEffect(() => {
    setActiveShortcutContexts?.(
      isEntityPopoverOpen ? [] : Object.keys(shortcuts)
    );
  }, [isEntityPopoverOpen, setActiveShortcutContexts, shortcuts]);

  const typeKey = ('planEntities.' + planningEntityType) as MrpEntityKey;
  const { control, getValues, setValue } =
    useFormContext<AppointmentFormFieldData>();
  const entityFormFields = useWatch({ name: typeKey, control });

  const closePopover = () => setIsEntityPopoverOpen(false);
  const openPopover = () => setIsEntityPopoverOpen(true);

  const onSelectEntity = (entity: PlanningEntityIdentifier, label: string) => {
    const fields = getValues(typeKey);

    // Prevent duplicates
    if (
      fields.some(
        ({ entityId, planningEntityType }) =>
          entityId == entity.entityId &&
          planningEntityType == entity.planningEntityType
      )
    )
      return;

    const updatedFields = [...fields, { ...entity, label, planningEntityType }];

    openCalendar([
      {
        label: label,
        planningEntityIdentifier: entity
      }
    ]);

    setValue(typeKey, updatedFields, { shouldDirty: true });
    closePopover();
  };

  const prevOpenState = useRef(isEntityPopoverOpen);

  useEffect(() => {
    // Focus after popover is closed
    if (prevOpenState.current && !isEntityPopoverOpen) {
      dummyInputRef.current?.focus();
    }
    prevOpenState.current = isEntityPopoverOpen;
  }, [isEntityPopoverOpen]);

  const emptyQueryOptions: EmptyQuerySearchOptions = {
    groupLabel: t('openedCalendars'),
    options: calendars
      .flatMap(({ sources }) => sources)
      .map((calendarData) => ({
        id: calendarData.planningEntityIdentifier.entityId!,
        label: calendarData.label,
        group: t(
          `entityTypeMap.${calendarData.planningEntityIdentifier.planningEntityType}.single`
        ),
        isLocal: true,
        entity: calendarData.planningEntityIdentifier
      }))
  };

  const sortedEntityIndexes = entityFormFields
    .map((_, index) => index)
    .sort((a, b) =>
      (entityFormFields[a].label ?? '').localeCompare(
        entityFormFields[b].label ?? ''
      )
    );

  return (
    <GridCompact6>
      <SectionHeader
        label={t(`entityTypeMap.${planningEntityType}.plural`)}
        icon={getEntityIcon(planningEntityType, undefined, 'MEDIUM')}
      />

      <Popover
        id={`entity-search-${planningEntityType}`}
        forceComponentWidth
        useTransition={false}
        useForcedState={true}
        forceOpen={isEntityPopoverOpen}
        onClose={closePopover}
        component={
          <EntitySearch
            activeEntities={entityFormFields.map(({ entityId }) => entityId!)}
            emptyQuerySearchOptions={emptyQueryOptions}
            filter={[planningEntityType]}
            minWidth={250}
            mode="compact"
            onChange={onSelectEntity}
            onEscape={closePopover}
            initialQuery={initialQuery}
          />
        }
      >
        <ClickHandler
          onClick={() => {
            setInitialQuery('');
            openPopover();
          }}
        >
          <EntitySearchInput
            ref={dummyInputRef}
            useAsDummy
            placeholder={
              t('searchEntity') +
              t(`entityTypeMap.${planningEntityType}.plural`).toLowerCase()
            }
            onEnterPress={openPopover}
            onCharacterInput={(character) => {
              setInitialQuery(character);
              openPopover();
            }}
          />
        </ClickHandler>
      </Popover>

      {sortedEntityIndexes.map((index) => (
        <SelectedEntity
          key={index}
          planningEntityType={planningEntityType}
          entityIndex={index}
        />
      ))}
    </GridCompact6>
  );
};

export default EntitySection;
